import React from "react";
import SignatureGroup from "./SignatureGroup";
import { Box, Divider, Typography } from "@mui/material";
import {
  DealDocumentSignature,
  DealLegalEntity,
  DealLegalEntityInvestment,
} from "../../../../api/generated-api";
import SignatureStatus from "./SignatureStatus";
import { useParams } from "react-router-dom";
import { DealRouteProps } from "../../../../routes/dealsRouting/DealsRoutes";
import InvestmentAndWireStatus from "./InvestmentAndWireStatus";
import { simpleSort } from "../../../../helpers/utils";

type InvestorSignatureGroupProps = {
  isUserCompanyMember: boolean;
  title: string;
  isLead: boolean;
  dealLegalEntitiesForInvestor: DealLegalEntity[];
  dealLegalEntityInvestmentsForInvestor: DealLegalEntityInvestment[];
  dealDocumentSignatures: DealDocumentSignature[];
  onOpenSignatureRequestPanel: (dealLegalEntityId: number) => void;
  onWireDateChange: (
    dealLegalEntityInvestmentId: number,
    wireDate: string
  ) => void;
};

const InvestorSignatureGroup = ({
  isUserCompanyMember,
  title,
  isLead,
  dealLegalEntitiesForInvestor,
  dealLegalEntityInvestmentsForInvestor,
  dealDocumentSignatures,
  onOpenSignatureRequestPanel,
  onWireDateChange,
}: InvestorSignatureGroupProps) => {
  const { orgId, dealId } = useParams<DealRouteProps>();

  if (!dealLegalEntitiesForInvestor || !dealLegalEntityInvestmentsForInvestor) {
    return null;
  }

  return (
    <SignatureGroup
      title={title}
      leftComponent={
        <Box display={"flex"} flexDirection={"column"}>
          {isLead && (
            <Box mt={1}>
              <Typography variant="table">Lead Investor</Typography>
            </Box>
          )}
          {dealLegalEntityInvestmentsForInvestor &&
            dealLegalEntityInvestmentsForInvestor.filter(
              (investment) => investment.exactWireAmount
            ) && (
              <Box mt={1}>
                <Typography variant="table">New Cash Investment</Typography>
              </Box>
            )}
          {dealLegalEntityInvestmentsForInvestor &&
            dealLegalEntityInvestmentsForInvestor.filter(
              (investment) => investment.purchaseAmount
            ) && (
              <Box mt={1}>
                <Typography variant="table">SAFE Conversion</Typography>
              </Box>
            )}
        </Box>
      }
      rightComponent={
        <Box display={"flex"} flexDirection={"column"} flex={1}>
          {dealLegalEntitiesForInvestor.map((dealLegalEntity) => {
            const dealLegalEntityInvestmentsForDealLegalEntity =
              dealLegalEntityInvestmentsForInvestor.filter(
                (dealLegalEntityInvestment) =>
                  dealLegalEntityInvestment.dealInvestorMemberSignerId ===
                  dealLegalEntity.id
              );

            const dealLegalEntityInvestmentsForDealLegalEntitySorted =
              dealLegalEntityInvestmentsForDealLegalEntity.sort((a, b) => {
                return simpleSort(b.exactWireAmount, a.exactWireAmount);
              });

            return (
              <Box
                key={dealLegalEntity.id}
                display={"flex"}
                flexDirection={"row"}
                marginTop={1}
                marginBottom={3}
                data-testid="signature-group-signer-row"
              >
                <Box flex={1}>
                  <SignatureStatus
                    orgId={+orgId}
                    dealId={+dealId}
                    isUserCompanyMember={isUserCompanyMember}
                    dealLegalEntity={dealLegalEntity}
                    dealDocumentSignatures={dealDocumentSignatures.filter(
                      (dealDocumentSignature) =>
                        dealDocumentSignature.dealLegalEntityId ===
                        dealLegalEntity.id
                    )}
                    isSelected={false}
                    rightComponent={
                      <Box flex={1}>
                        {dealLegalEntityInvestmentsForDealLegalEntitySorted.map(
                          (dealLegalEntityInvestment) => {
                            return (
                              <Box
                                key={`investment-wire-status-${dealLegalEntityInvestment.id}`}
                                marginBottom={3}
                              >
                                <InvestmentAndWireStatus
                                  isUserCompanyMember={isUserCompanyMember}
                                  dealLegalEntityInvestment={
                                    dealLegalEntityInvestment
                                  }
                                  onWireDateChange={onWireDateChange}
                                />
                              </Box>
                            );
                          }
                        )}
                      </Box>
                    }
                    onOpenSignatureRequestPanel={() => {
                      onOpenSignatureRequestPanel(dealLegalEntity.id);
                    }}
                  />
                  <Box mb={5}>
                    <Divider />
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      }
    />
  );
};

export default InvestorSignatureGroup;
